import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Lock, Shield } from 'lucide-react';
import { ProspectShell } from '../../components/prospect/ProspectShell';

// Where an invite link lands (TYR-204/208). One screen: what the scan reads,
// what happens to it, what never happens, the authorisation checkbox, and the
// production or sandbox choice. Opening the link puts the lead on the session;
// Connect starts the scan-intent OAuth flow, whose callback runs the audit,
// snapshots it, and discards the Salesforce tokens.
//
// Every sentence here is checked against the code. Change the wording and the
// CONSENT_VERSION in lead.service.ts changes with it.
export function ScanConsentPage() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const [state, setState] = useState<'checking' | 'ready' | 'expired'>('checking');
  const [lead, setLead] = useState<{ name: string; company: string } | null>(null);
  const [consented, setConsented] = useState(false);
  const [orgType, setOrgType] = useState<'production' | 'sandbox'>('production');
  const needsConsent = params.get('error') === 'consent-required';
  // The invite is validated once per mount; StrictMode double-mounts effects
  // in dev and a second POST is wasted work.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    // Strip whitespace and literal "\n"/"\r" escapes picked up by copy-pasting
    // the link out of an email (same failure mode as the hub activation link).
    const clean = (s: string | null | undefined): string | undefined =>
      s ? s.replace(/(\\[rn])+$/g, '').trim() : undefined;
    const safeToken = clean(token);
    const leadId = clean(params.get('lead'));
    if (!safeToken || !leadId) {
      setState('expired');
      return;
    }
    fetch('/api/prospect/invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: safeToken, lead: leadId }),
    })
      .then(async (res) => {
        if (!res.ok) {
          setState('expired');
          return;
        }
        const json = (await res.json()) as { lead?: { name: string; company: string } };
        setLead(json.lead ?? null);
        setState('ready');
      })
      .catch(() => setState('expired'));
  }, [token, params]);

  const handleConnect = (e: React.FormEvent) => {
    e.preventDefault();
    if (!consented) return;
    // Full-page nav (same origin) carries the session cookie so the callback
    // knows which lead is connecting. intent=prospect routes the callback into
    // the snapshot-and-discard branch, never the consultant one. consent=1
    // affirms the scan authorisation; the server records it (version, IP and
    // time) before it will redirect to Salesforce. orgType picks
    // login.salesforce.com or test.salesforce.com.
    const qs = new URLSearchParams({ intent: 'prospect', consent: '1', orgType });
    window.location.href = `/api/auth/connect?${qs.toString()}`;
  };

  if (state === 'checking') {
    return (
      <ProspectShell>
        <div className="min-h-[50vh] flex items-center justify-center">
          <p className="opacity-70 text-sm">Opening your invite…</p>
        </div>
      </ProspectShell>
    );
  }

  if (state === 'expired') return <ExpiredLink />;

  return (
    <ProspectShell>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="max-w-[880px] mx-auto py-12"
      >
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-grow mb-3">
          Invited scan
        </div>
        <h1 className="text-[34px] font-bold tracking-[-0.03em] leading-tight">
          {lead?.company
            ? `Before you connect ${lead.company}'s Salesforce org, here is exactly what happens.`
            : 'Before you connect your Salesforce org, here is exactly what happens.'}
        </h1>

        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Box title="What the scan reads">
            <li>
              Configuration: objects, fields, record types, layouts, sharing settings, flows, rules,
              Apex and its test coverage, packages, profiles, permission sets, and integrations.
            </li>
            <li>Record counts, storage limits, and the size and owner of your largest files.</li>
            <li>Your user list and 90 days of login history, to count active and idle licences.</li>
            <li>
              Recent flow errors, to show which automation is failing. The interview label and the
              name of the person who hit the error are stripped before anything is stored.
            </li>
            <li>
              A sample of up to 5,000 email fields on Contact and Lead, and a small sample of Account
              phone and address fields, to measure data quality.
            </li>
          </Box>
          <Box title="What happens to it">
            <li>The samples and the login history become counts in memory, then are dropped.</li>
            <li>
              Names, usernames, emails, file names and flow interview labels are stripped before
              anything is stored.
            </li>
            <li>
              We keep the results for 6 months: counts, scores, configuration names, and per-user
              activity rows without names. You can delete them from the results page.
            </li>
            <li>
              The AI report gets counts, scores and your org name. No record values, no names of
              people.
            </li>
            <li>
              We keep a record of this approval: the terms version, time and IP address, the email
              the invite was sent to, and the Salesforce username and org ID of the person who
              approves.
            </li>
          </Box>
          <Box title="What never happens">
            <li>No write, update or delete in your org. OrgIQ sends read requests only.</li>
            <li>No Salesforce password or token is stored.</li>
            <li>We ask Salesforce to revoke our access when the scan finishes.</li>
            <li>No second visit. A rescan needs a new link and a new login from you.</li>
          </Box>
        </div>

        <div className="mt-4 bg-surface-3 border border-border px-5 py-4 text-[12.5px] leading-relaxed text-neutral-400">
          <span className="text-crux-light font-medium">
            Salesforce will ask you to allow &ldquo;Manage user data via APIs&rdquo;.
          </span>{' '}
          Salesforce has no read-only permission for apps, so every audit tool gets this wording.
          OrgIQ sends read requests only. To enforce that on your side, log in with a user that has a
          read-only profile. The scan works the same.
        </div>

        <form onSubmit={handleConnect} className="mt-8 bg-surface-2 border border-border px-6 py-6">
          <fieldset>
            <legend className="text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500 mb-2.5">
              Which org are you connecting?
            </legend>
            <div className="flex flex-wrap gap-2.5">
              <OrgChoice
                label="Production"
                hint="login.salesforce.com"
                checked={orgType === 'production'}
                onSelect={() => setOrgType('production')}
              />
              <OrgChoice
                label="Sandbox"
                hint="test.salesforce.com"
                checked={orgType === 'sandbox'}
                onSelect={() => setOrgType('sandbox')}
              />
            </div>
          </fieldset>

          <label className="flex items-start gap-2.5 cursor-pointer select-none mt-6">
            <input
              type="checkbox"
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-sfblue"
            />
            <span className="text-[12.5px] leading-snug text-neutral-400">
              I am authorised to approve this one-time scan of this Salesforce org for my
              organisation, and I agree to OrgIQ&apos;s{' '}
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-crux-light underline underline-offset-2 hover:opacity-80"
              >
                data handling and scan terms, including the limit on TYRL&apos;s liability
              </Link>
              .
            </span>
          </label>

          {needsConsent && !consented ? (
            <p className="mt-3 text-[12px] text-sunset">
              Tick the box above before you connect. Nothing was scanned.
            </p>
          ) : null}

          <button
            type="submit"
            disabled={!consented}
            className="w-full sm:w-auto h-12 mt-5 px-7 bg-sfblue hover:opacity-90 active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100 text-white font-semibold text-sm rounded-[10px] inline-flex items-center justify-center gap-2.5 transition-all tracking-[0.01em]"
          >
            <ArrowRight size={16} className="shrink-0" />
            Connect with Salesforce
          </button>

          <div className="flex flex-wrap items-center gap-4 pt-5">
            <SecurityItem icon={<Shield size={12} />}>Secure OAuth 2.0</SecurityItem>
            <SecurityItem icon={<Clock size={12} />}>Read requests only</SecurityItem>
            <SecurityItem icon={<Lock size={12} />}>Access revoked after</SecurityItem>
          </div>
          <p className="text-[12px] text-neutral-500 mt-4">
            Questions first? Reply to the email that brought you here. Nothing starts until you press
            the button.
          </p>
        </form>
      </motion.div>
    </ProspectShell>
  );
}

// One plain screen for every dead link: expired, already used, or unknown.
// No form, and OrgIQ sends no replacement by itself.
export function ExpiredLink() {
  return (
    <ProspectShell>
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="max-w-md text-center px-6">
          <h1 className="text-2xl font-semibold mb-3">This link has expired.</h1>
          <p className="text-sm text-neutral-400 leading-relaxed">
            Ask Cyril Duvignau for a new one. A scan link is valid for 24 hours and allows one scan.
          </p>
        </div>
      </div>
    </ProspectShell>
  );
}

function Box({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface-2 border border-border px-5 py-5">
      <h2 className="text-sm font-semibold mb-2.5">{title}</h2>
      <ul className="space-y-1.5 list-disc pl-4 marker:text-neutral-600 text-[12.5px] leading-relaxed text-neutral-400">
        {children}
      </ul>
    </div>
  );
}

function OrgChoice({
  label,
  hint,
  checked,
  onSelect,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={`flex items-center gap-2.5 px-4 py-2.5 border cursor-pointer select-none ${
        checked ? 'border-sfblue bg-sfblue/10' : 'border-border bg-surface-3'
      }`}
    >
      <input
        type="radio"
        name="orgType"
        checked={checked}
        onChange={onSelect}
        className="h-4 w-4 shrink-0 accent-sfblue"
      />
      <span className="text-[13px] text-crux-light font-medium">{label}</span>
      <span className="text-[11px] text-neutral-500">{hint}</span>
    </label>
  );
}

function SecurityItem({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-neutral-500">
      <span className="shrink-0">{icon}</span>
      <span>{children}</span>
    </div>
  );
}
